/**
 * Auth header storage and injection for fetch requests.
 *
 * Manages the X-Auth-Cookie header value (which contains the ZedToken for SpiceDB
 * authorization) in browser memory and localStorage. The header name is
 * intentionally obscured.
 */

const AUTH_HEADER_STORAGE_KEY = 'sweetspot.auth_cookie_header'
export const AUTH_COOKIE_HEADER_UPDATED_EVENT =
  'sweetspot:auth-cookie-header-updated'

type StoredAuthHeader = {
  value: string
  generatedAtMs?: number
}

function getAuthHeaderStorage(): Storage | null {
  if (typeof window === 'undefined') return null

  try {
    return window.localStorage
  } catch {
    return null
  }
}

function readPersistedHeader(): StoredAuthHeader | null {
  const storage = getAuthHeaderStorage()
  if (!storage) return null

  let rawValue: string | null
  try {
    rawValue = storage.getItem(AUTH_HEADER_STORAGE_KEY)
  } catch {
    return null
  }

  if (!rawValue) return null

  try {
    const parsed = JSON.parse(rawValue) as Partial<StoredAuthHeader>
    if (typeof parsed.value !== 'string') {
      storage.removeItem(AUTH_HEADER_STORAGE_KEY)
      return null
    }

    const generatedAtMs =
      typeof parsed.generatedAtMs === 'number' &&
      Number.isFinite(parsed.generatedAtMs)
        ? parsed.generatedAtMs
        : undefined

    return {
      value: parsed.value,
      generatedAtMs,
    }
  } catch {
    try {
      storage.removeItem(AUTH_HEADER_STORAGE_KEY)
    } catch {
      // Ignore storage cleanup failures.
    }
    return null
  }
}

function persistHeader(value: string, generatedAtMs?: number): void {
  const storage = getAuthHeaderStorage()
  if (!storage) return

  const payload: StoredAuthHeader =
    generatedAtMs !== undefined ? { value, generatedAtMs } : { value }

  try {
    storage.setItem(AUTH_HEADER_STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // Ignore storage write failures.
  }
}

function setWindowStoredHeader(value: string, generatedAtMs?: number): void {
  if (typeof window === 'undefined') return

  window.__AUTH_COOKIE_HEADER_VALUE__ = value
  if (generatedAtMs !== undefined) {
    window.__AUTH_COOKIE_HEADER_GENERATED_AT_MS__ = generatedAtMs
  } else {
    delete window.__AUTH_COOKIE_HEADER_GENERATED_AT_MS__
  }
}

function dispatchStoredHeaderUpdated(
  value: string,
  generatedAtMs?: number,
): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent(AUTH_COOKIE_HEADER_UPDATED_EVENT, {
      detail: { value, generatedAtMs },
    }),
  )
}

function replaceStoredHeader(value: string, generatedAtMs?: number): void {
  setWindowStoredHeader(value, generatedAtMs)
  persistHeader(value, generatedAtMs)
  dispatchStoredHeaderUpdated(value, generatedAtMs)
}

function hydrateStoredHeaderFromLocalStorage(): void {
  if (typeof window === 'undefined') return
  if (window.__AUTH_COOKIE_HEADER_VALUE__ !== undefined) return

  const persistedHeader = readPersistedHeader()
  if (!persistedHeader) {
    delete window.__AUTH_COOKIE_HEADER_GENERATED_AT_MS__
    return
  }

  setWindowStoredHeader(persistedHeader.value, persistedHeader.generatedAtMs)
}

/**
 * Retrieves the stored auth header value from browser memory.
 * Used by WebSocket connections and other code that needs the ZedToken.
 *
 * @returns The stored header value, or undefined if not set
 */
export function getStoredHeader(): string | undefined {
  if (typeof window === 'undefined') return undefined
  hydrateStoredHeaderFromLocalStorage()
  return window.__AUTH_COOKIE_HEADER_VALUE__
}

export function getStoredHeaderGeneratedAtMs(): number | undefined {
  if (typeof window === 'undefined') return undefined
  hydrateStoredHeaderFromLocalStorage()
  return window.__AUTH_COOKIE_HEADER_GENERATED_AT_MS__
}

/**
 * Stores an auth header value in browser memory.
 * Called automatically by the fetch wrapper when responses include the header.
 *
 * @param value - The header value to store. Null/undefined values are ignored.
 */
export function setStoredHeader(
  value: string | null | undefined,
  generatedAtMs?: number,
): void {
  if (typeof window === 'undefined') return
  if (value != null) {
    hydrateStoredHeaderFromLocalStorage()
    const currentGeneratedAtMs = window.__AUTH_COOKIE_HEADER_GENERATED_AT_MS__
    if (currentGeneratedAtMs !== undefined) {
      if (
        generatedAtMs === undefined ||
        generatedAtMs <= currentGeneratedAtMs
      ) {
        return
      }
    }
    replaceStoredHeader(value, generatedAtMs)
  }
}

/**
 * Stores an authoritative auth header value from an explicit bootstrap source
 * such as a Slack deep-link query param. This intentionally overrides any
 * persisted token and clears generatedAt ordering state.
 */
export function setBootstrapStoredHeader(
  value: string | null | undefined,
): void {
  if (value == null) return
  replaceStoredHeader(value)
}
